import type { ResizableColumnDef } from '@protoss/ui';
import { BarChartIcon, Button, IconSize, ResizableTable } from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { PageErrorState } from '../components/shared/PageErrorState';
import { AppShell, MainContentShell } from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { SalesHeaderSearch } from '../features/sales/components/SalesHeaderSearch';
import type { SalesCompanySummary } from '../features/sales/services/salesApi';
import { salesApi } from '../features/sales/services/salesApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './deal-page.css';

const PAGE_SIZE = 50;

function SalesCompaniesPageContent() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);

  const [companies, setCompanies] = React.useState<SalesCompanySummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const loadCompanies = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await salesApi.listCompanies();
      setCompanies(result.companies);
    } catch {
      setError('Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleSort = React.useCallback(
    (key: string) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDir('asc');
      } else if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortKey(null);
        setSortDir('asc');
      }
    },
    [sortKey, sortDir]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredCompanies = React.useMemo(() => {
    if (!normalizedQuery) return companies;
    return companies.filter((company) => {
      const haystack = [company.name, company.domain, company.industry, company.headquartersCity]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [companies, normalizedQuery]);

  const sortedCompanies = React.useMemo(() => {
    if (!sortKey) return filteredCompanies;
    return [...filteredCompanies].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortKey === 'name') {
        aVal = a.name;
        bVal = b.name;
      } else if (sortKey === 'industry') {
        aVal = a.industry ?? '';
        bVal = b.industry ?? '';
      } else if (sortKey === 'employeeCount') {
        aVal = String(a.employeeCount ?? 0).padStart(12, '0');
        bVal = String(b.employeeCount ?? 0).padStart(12, '0');
      } else if (sortKey === 'updatedAt') {
        aVal = a.updatedAt ?? '';
        bVal = b.updatedAt ?? '';
      }
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredCompanies, sortKey, sortDir]);

  const paginatedCompanies = React.useMemo(
    () => sortedCompanies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedCompanies, page]
  );

  const columns = React.useMemo<ResizableColumnDef<SalesCompanySummary>[]>(
    () => [
      {
        key: 'name',
        label: 'Company',
        defaultWidth: 240,
        minWidth: 120,
        sortable: true,
        render: (company) => <span className="sales-table__primary">{company.name}</span>,
      },
      {
        key: 'domain',
        label: 'Domain',
        defaultWidth: 180,
        minWidth: 80,
        render: (company) => (
          <span className="sales-table__secondary">{company.domain ?? '-'}</span>
        ),
      },
      {
        key: 'industry',
        label: 'Industry',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (company) => (
          <span className="sales-table__secondary">{company.industry ?? '-'}</span>
        ),
      },
      {
        key: 'location',
        label: 'Location',
        defaultWidth: 200,
        minWidth: 100,
        render: (company) => {
          const parts = [company.headquartersCity, company.headquartersState].filter(Boolean);
          return (
            <span className="sales-table__secondary">
              {parts.length > 0 ? parts.join(', ') : '-'}
            </span>
          );
        },
      },
      {
        key: 'employeeCount',
        label: 'Employees',
        defaultWidth: 120,
        minWidth: 80,
        sortable: true,
        render: (company) => (
          <span className="sales-table__secondary">
            {company.employeeCount != null ? company.employeeCount.toLocaleString() : '-'}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        label: 'Updated',
        defaultWidth: 140,
        minWidth: 80,
        sortable: true,
        render: (company) => (
          <span className="sales-table__date">
            {company.updatedAt ? new Date(company.updatedAt).toLocaleDateString() : '-'}
          </span>
        ),
      },
    ],
    []
  );

  if (isLoadingWorkspace) {
    return null;
  }

  const leftNav = (
    <AppNav
      selected="companies"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const headerActions = (
    <>
      <SalesHeaderSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search companies…"
        ariaLabel="Search companies"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void loadCompanies()}
        disabled={loading}
      >
        Refresh
      </Button>
    </>
  );

  const main = (
    <MainContentShell title="Companies" actions={headerActions}>
      {error ? (
        <PageErrorState message={error} onRetry={() => void loadCompanies()} />
      ) : (
        <div className="sales-table__container">
          <ResizableTable
            columns={columns}
            rows={paginatedCompanies}
            getRowKey={(company) => company.uuid}
            aria-label="Companies"
            sortKey={sortKey ?? undefined}
            sortDir={sortDir}
            onSort={handleSort}
            loading={loading}
            emptyState={
              <div className="sales-table__empty">
                <BarChartIcon
                  width={IconSize.xl}
                  height={IconSize.xl}
                  aria-hidden="true"
                  className="sales-table__empty-icon"
                />
                <span>
                  {normalizedQuery ? 'No companies match your search.' : 'No companies yet.'}
                </span>
              </div>
            }
            pagination={
              sortedCompanies.length > PAGE_SIZE
                ? {
                    page,
                    pageSize: PAGE_SIZE,
                    total: sortedCompanies.length,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </div>
      )}
    </MainContentShell>
  );

  return <AppShell leftNav={leftNav} main={main} />;
}

export function SalesCompaniesPage() {
  return <SalesCompaniesPageContent />;
}
